import React from 'react';
import { Modal, Spin } from 'antd';
import { cn } from '@/lib/utils';

export type ApprovalConfirmModalRow = {
  label: React.ReactNode;
  value?: React.ReactNode;
  valueClassName?: string;
};

type ApprovalConfirmModalProps = {
  open: boolean;
  title: React.ReactNode;
  rows: ApprovalConfirmModalRow[];
  rejectLabel: React.ReactNode;
  confirmLabel: React.ReactNode;
  rejectLoading?: boolean;
  confirmLoading?: boolean;
  disabled?: boolean;
  onReject: () => void;
  onConfirm: () => void;
  onClose: () => void;
  width?: number;
};

export function ApprovalConfirmModal({
  open,
  title,
  rows,
  rejectLabel,
  confirmLabel,
  rejectLoading,
  confirmLoading,
  disabled,
  onReject,
  onConfirm,
  onClose,
  width = 460,
}: ApprovalConfirmModalProps) {
  const isBusy = disabled || rejectLoading || confirmLoading;

  return (
    <Modal
      visible={open}
      onCancel={onClose}
      footer={null}
      width={width}
      centered
      closable={!isBusy}
      maskClosable={!isBusy}
      title={null}
      bodyStyle={{ padding: 0 }}
    >
      <div className="rounded-2xl bg-white px-8 pb-7 pt-8 max-sm:px-5">
        <h2 className="mb-6 mt-0 text-center text-2xl font-bold leading-7 text-[#111111] max-sm:text-xl">
          {title}
        </h2>
        <div className="space-y-3">
          {rows.map((row, index) => (
            <div
              key={index}
              className="grid grid-cols-[145px_minmax(0,1fr)] items-center gap-4 text-base leading-6 max-sm:grid-cols-1 max-sm:gap-1"
            >
              <div className="text-[#8c8c8c]">{row.label}</div>
              <div
                className={cn(
                  'min-w-0 break-words font-bold text-[#111111]',
                  row.valueClassName,
                )}
              >
                {row.value ?? '--'}
              </div>
            </div>
          ))}
        </div>
        <div className="mt-7 grid grid-cols-2 gap-3 max-sm:grid-cols-1">
          <button
            type="button"
            disabled={isBusy}
            className="h-12 rounded-xl border border-solid border-[#9b9b9b] bg-white text-base font-bold text-[#8c8c8c] outline-none disabled:opacity-60"
            onClick={onReject}
          >
            {rejectLoading ? <Spin size="small" /> : rejectLabel}
          </button>
          <button
            type="button"
            disabled={isBusy}
            className="h-12 rounded-xl border-0 bg-[linear-gradient(90deg,#ff6b1a_0%,#ffb21a_100%)] text-base font-bold text-white outline-none disabled:opacity-60"
            onClick={onConfirm}
          >
            {confirmLoading ? <Spin size="small" /> : confirmLabel}
          </button>
        </div>
      </div>
    </Modal>
  );
}
